import { useUserStore } from '@/stores/user';
import { useAnonymousUserStore } from '@/stores/anonymousUser';
import { useAccountStore } from '@/stores/account';
import { useDatasetStore } from '@/stores/dataset';
const defaultRoute = process.env.VUE_APP_DEFAULT_ROUTE;
import { popupBuilderWelcome } from '@/utils/popups';
import { mapCurrentDataset, mapLegacyDataset } from '@/utils/builder';

export function ifNotAuthenticatedRedirect(to, from, next) {
  const userStore = useUserStore();

  if (!userStore.isAuthenticated) {
    userStore.setRedirectAfterLogin(to.fullPath); 
    next({ name: 'login' });
  } else {
    next();
  }
}

export function ifAuthenticatedRedirect(to, from, next) {
    const userStore = useUserStore();
  
    if (userStore.isAuthenticated) {
      next({ name: defaultRoute });
    } else {
      next();
    }
}

export function ifNotAuthenticatedAnonymousRedirect(to, from, next) {
    const userStore = useUserStore();
  
    if (!userStore.isAuthenticated) {
        const anonymousUserStore = useAnonymousUserStore();

        if (!anonymousUserStore.isAuthenticated) {
            anonymousUserStore.setRedirectAfterLogin(to.fullPath);
            next({ name: 'anonymous-login' });
        } else {
            next();
        }
    } else {
      next();
    }
}

export function hasQueryParamPasswordSelect(to, from, next) {
    if (!to.query.r) {
      next({ name: 'passwordReset' });
    } else {
      next();
    }
}

export async function hasQueryParamsOrder(to, from, next) {
    if (!to.query.order_id && !to.query.account_id) {
        next({ name: 'subscription-order' });
    } else {
        const accountStore = useAccountStore();
        const userStore = useUserStore();
        const accountId = to.query.account_id;
  
        if (accountId) {
            accountStore.changeAccount(accountId);
        }

        const account = await accountStore.getAccount();

        if(!account) {
            next({ name: 'accounts' });
        } else if(userStore.isPartnerAdmin || account.role < 15) {
            next();
        } else {
            next({ name: defaultRoute });
        }
    }
}

export async function hasAccountId(to, from, next) {
    const accountStore = useAccountStore();
    const accountId = to.query.account_id;
    const currentAccountId = accountStore.hasAccountId ? accountStore.account.account_id : null;
  
    if (accountId && accountId !== currentAccountId) {
      accountStore.changeAccount(accountId);
    }

    if (accountId) {
        // If it's ever needed to clean the URL from account_id param:
        // delete to.query.account_id;
        // next({ path: to.path, query: to.query, params: to.params });
        next();
    } else if (accountStore.hasAccountId) {
        next();
    } else {
        next({ name: 'accounts' });
    }
}

export function hasDataset(to, from, next) {
    let dataset = to.query.bd_ds || null;
    let dataset_legacy = to.query.dataset || null;
    let name = to.query.dataset_name || null;
    let disablePopup = to.query.disable_popup === '1' || to.query.disable_popup === 'true';

    if (dataset || dataset_legacy) {
        let parsedDataset = null;

        try {
            if(dataset) {
                parsedDataset = mapCurrentDataset(JSON.parse(dataset));
            } else if(dataset_legacy) {
                parsedDataset = mapLegacyDataset(JSON.parse(dataset_legacy));
            }
            
            if (parsedDataset) {
                useDatasetStore().overwriteDataset(parsedDataset, name, disablePopup);
            }
        } catch {
            // Do nothing.
        }

        delete to.query.bd_ds;
        delete to.query.dataset;
        delete to.query.dataset_name;
        delete to.query.disable_popup;
        next({ path: to.path, query: to.query });
    } else {
        next();
    }
}

export function hasDatasetOrderId(to, from, next) {
    const orderId = to.query.order_id;
  
    if (orderId) {
        next();
    } else {
        next({ name: 'builder' });
    }
}

export function welcomePopupBuilder(to, from, next) {
    let dataset = to.query.dataset || null;
    const welcomePopupShown = localStorage.getItem('bd_welcome_popup_shown');
    
    if (!welcomePopupShown) {
        if (!dataset) {
            popupBuilderWelcome();
        }
        localStorage.setItem('bd_welcome_popup_shown', 'true');
    }
    next();
}

export async function hasAccountAdminRights(to, from, next) {
    const userStore = useUserStore();

    if(userStore.isPartnerAdmin) {
        next();
    } else {
        const accountStore = useAccountStore();
        const account = await accountStore.getAccount();

        if(account?.role < 15) {
            next();
        } else {
            next({ name: defaultRoute });
        }
    }
}

export async function hasPartnerAdminRights(to, from, next) {
    const userStore = useUserStore();

    if(userStore.isPartnerAdmin) {
        next();
    } else {
        const accountStore = useAccountStore();
        if (accountStore.hasAccountId) {
            next({ name: defaultRoute });
        } else {
            next({ name: 'accounts' });
        }
    }
}

// export async function isActiveAccount(to, from, next) {
//     const userStore = useUserStore();

//     if(userStore.isPartnerAdmin) {
//         next();
//     } else {
//         const accountStore = useAccountStore();
//         const account = await accountStore.getAccount();

//         if(account?.status > 0) {
//             next();
//         } else {
//             next({ name: defaultRoute });
//         }
//     }
// }

export async function hasPaywall(to, from, next) {
    const userStore = useUserStore();

    if(userStore.isPartnerGod) {
        next();
    } else {
        const accountStore = useAccountStore();
        const account = await accountStore.getAccount();

        if(account && !accountStore.paywall) {
            next();
        } else {
            next({ name: defaultRoute });
        }
    }
}

export async function hasEndpointLlmTemplate(to, from, next) {
    const userStore = useUserStore();

    if(userStore.isPartnerGod) {
        next();
    } else {
        const accountStore = useAccountStore();
        const account = await accountStore.getAccount();

        if(account && accountStore.endpointLlmTemplate) {
            next();
        } else {
            next({ name: defaultRoute });
        }
    }
}

export async function accountHasSubscription(to, from, next) {
    const accountStore = useAccountStore();

    if(accountStore.hasSubscriptions) {
        if(accountStore.subscriptions[0].status === 'canceled') {
            next({ name: 'subscriptions' });
        } else {
            next();
        }
    } else {
        const subscriptions = await accountStore.getSubscriptions();

        if(subscriptions?.length > 0 && subscriptions[0].status !== 'canceled') {
            next();
        } else {
            next({ name: 'subscriptions' });
        }
    }
}

export async function accountHasNoSubscription(to, from, next) {
    const accountStore = useAccountStore();

    if(accountStore.hasSubscriptions) {
        if(accountStore.subscriptions[0].status === 'canceled') {
            next();
        } else {
            next({ name: 'subscriptions' });
        }
    } else {
        const subscriptions = await accountStore.getSubscriptions();

        if(!subscriptions || subscriptions.length < 1 || subscriptions[0].status === 'canceled') {
            next();
        } else {
            next({ name: 'subscriptions' });
        }
    }
}

export function setRecoveryDeepLink(to, router) {
  const api = window.__SESSION_RECOVERY__;
  if (!api || !api.setDeepLink || !api.clearDeepLink) return;

  const accountStore = useAccountStore();
  const accountId =
    (to.query && to.query.account_id) ||
    (accountStore && accountStore.hasAccountId && accountStore.account && accountStore.account.account_id) ||
    null;

  const origin = window.location.origin;
  const abs = (loc) => origin + router.resolve(loc).href;

  const datasetStore = useDatasetStore();
  const shareLink = datasetStore.shareLink || null;

  let href = null;

  switch (to.name) {
    // case 'company': {
    //   const query = accountId ? { account_id: accountId } : undefined;
    //   href = abs({ name: 'dashboard', query });
    //   break;
    // }
    // case 'audienceCompany': {
    //   const audienceId = to.params && to.params.id;
    //   if (audienceId) {
    //     const query = accountId ? { account_id: accountId } : undefined;
    //     href = abs({ name: 'audience', params: { id: audienceId }, query });
    //   }
    //   break;
    // }
    case 'accounts': {
        href = null;
        break;
    }
    case 'builder':
    case 'specification':
    case 'order-method':
    case 'order-checkout':
    case 'consult':
        if (datasetStore && !!datasetStore.dataset && !datasetStore.hasDefaultDataset && !!shareLink) {
            const url = new URL(shareLink, origin);
            const bd_ds = url.searchParams.get('bd_ds') || null;
            const query = {};
            if (bd_ds) {
                query.bd_ds = bd_ds,
                query.disable_popup = '1'
            }
            href = abs({ name: to.name, query });
        }
        break;
    case 'order-consult-requested':
        href = abs({ name: 'builder' });
        break;
    default: {
      if (accountId) {
        const curr = router.resolve(to);
        const url = new URL(curr.href, origin);
        url.searchParams.set('account_id', accountId);
        href = url.href;
      }
    }
  }

  if (href) {
    api.setDeepLink(href);
    console.log('Deep link:', href);
  } else api.clearDeepLink();
}